import { Tab } from 'tab-election'
import {
  Job,
  StateMachineContext,
  JobEvent,
  JobOptions,
  JobStatus,
  QueueStats,
  StateMachineState,
} from './types.js'
import { JobQueueDatabase } from './JobQueueDatabase.js'
import { getErrorMessage } from '../errors'
import { StateActor } from './StateActor'

interface JobQueueServiceOptions<
  State extends StateMachineState,
  Context extends StateMachineContext
> {
  resolveContext?: (state: State, job: Job) => Promise<Context>
}

export class StateMachineService<
  State extends StateMachineState,
  Context extends StateMachineContext
> {
  private options: JobQueueServiceOptions<State, Context>
  private hub: Tab | undefined
  private database: JobQueueDatabase
  private worker: Worker | null = null
  private handlers = new Map<
    string,
    StateActor<unknown, State, Context, unknown>
  >()
  private runningJobs = new Map<string, { timeoutId?: number }>()
  private processingTimeout: number | null = null
  private isProcessing = false

  constructor(options: JobQueueServiceOptions<State, Context>) {
    this.options = options
    this.database = new JobQueueDatabase()
  }

  async init(hub: Tab): Promise<void> {
    this.hub = hub
    await this.database.init()
    this.startRegularCleanup()
  }

  private emitJobEvent(
    type: JobEvent['type'],
    job: Job,
    result: unknown = null
  ): void {
    this.hub?.dispatchEvent(
      new CustomEvent('job-event', {
        detail: {
          type,
          job,
          result,
        },
      })
    )
  }

  private setState(state: Partial<State>) {
    if (!this.hub) {
      throw new Error(
        'Cannot set state before initialising the service'
      )
    }
    if (!this.hub.isLeader) return
    this.hub.setState({
      ...this.hub.getState(),
      ...state,
    })
  }

  async enqueue<T>(
    type: string,
    data: T,
    options: JobOptions = {}
  ): Promise<string> {
    if (!type || !this.handlers.has(type)) {
      throw new Error(`Invalid Job Type: ${type}`)
    }
    const job: Job = {
      id: crypto.randomUUID(),
      queueId: options.queueId || 'default',
      type,
      data,
      priority: options.priority || 0,
      attempts: 0,
      maxAttempts: options.maxAttempts || 3,
      scheduledAt: Date.now() + (options.delay || 0),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      status: JobStatus.PENDING,
      timeout: options.timeout,
      result: undefined,
    }
    const throttle = options.throttle ?? 500
    if (throttle > 0) {
      const pending = await this.database.getJobsByStatus(
        JobStatus.PENDING,
        job.type,
        job.queueId
      )
      const between = [
        job.scheduledAt - throttle,
        job.scheduledAt + throttle,
      ]
      const shouldThrottle = pending.find(
        (j) =>
          j.scheduledAt > between[0] &&
          j.scheduledAt < between[1]
      )
      if (shouldThrottle) {
        console.log(
          `JobQueueService:throttle found ${
            shouldThrottle.type
          } scheduled at ${new Date(shouldThrottle.scheduledAt)}`
        )
        return shouldThrottle.id
      }
    }

    await this.database.addJob(job)
    this.emitJobEvent('job-created', job)

    return job.id
  }

  registerActor<T>(
    manager: StateActor<T, State, Context, unknown>
  ) {
    this.handlers.set(
      manager.name,
      manager as unknown as StateActor<
        unknown,
        State,
        Context,
        unknown
      >
    )
  }

  async cancelJob(jobId: string): Promise<boolean> {
    const job = await this.database.getJob(jobId)
    if (!job) return false

    if (job.status === JobStatus.PENDING) {
      // Cancel pending job
      job.status = JobStatus.CANCELLED
      job.updatedAt = Date.now()
      await this.database.updateJob(job)
      this.emitJobEvent('job-cancelled', job, null)
    } else {
      console.warn(
        'Can only cancel pending jobs, not',
        job.status
      )
    }

    return true
  }

  async getJob(jobId: string): Promise<Job | undefined> {
    return this.database.getJob(jobId)
  }

  async getStats(): Promise<QueueStats> {
    // const stats = await this.database.getStats()
    const queueStats: QueueStats = {}

    // Group by queue and calculate stats
    let jobs = await this.database.getJobsByStatus(
      JobStatus.PENDING
    )
    jobs = jobs.concat(
      await this.database.getJobsByStatus(JobStatus.PROCESSING),
      await this.database.getJobsByStatus(JobStatus.COMPLETED),
      await this.database.getJobsByStatus(JobStatus.FAILED),
      await this.database.getJobsByStatus(JobStatus.CANCELLED)
    )

    const queueGroups = new Map<string, Job[]>()
    jobs.forEach((job) => {
      if (!queueGroups.has(job.queueId)) {
        queueGroups.set(job.queueId, [])
      }
      queueGroups.get(job.queueId)!.push(job)
    })

    queueGroups.forEach((jobs, queueId) => {
      queueStats[queueId] = {
        pending: jobs.filter(
          (j) => j.status === JobStatus.PENDING
        ).length,
        processing: jobs.filter(
          (j) => j.status === JobStatus.PROCESSING
        ).length,
        completed: jobs.filter(
          (j) => j.status === JobStatus.COMPLETED
        ).length,
        failed: jobs.filter((j) => j.status === JobStatus.FAILED)
          .length,
        cancelled: jobs.filter(
          (j) => j.status === JobStatus.CANCELLED
        ).length,
        total: jobs.length,
      }
    })

    return queueStats
  }

  async cleanup(olderThan?: number): Promise<number> {
    return this.database.cleanup(olderThan)
  }

  private startRegularCleanup() {
    this.cleanup().then(async () => {
      console.log(
        'JobQueueService:cleaned',
        await this.getStats()
      )
      setTimeout(() => {
        this.startRegularCleanup()
      }, 60 * 60_000)
    })
  }

  startProcessing = () => {
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout)
    }
    if (this.isProcessing) return
    this.isProcessing = true

    this.processJobs()
      .catch((error) => {
        console.error('Error processing jobs:', error)
      })
      .finally(() => {
        this.isProcessing = false
        this.processingTimeout = setTimeout(
          this.startProcessing,
          1000
        ) as unknown as number
      })
  }

  private async processJobs(): Promise<void> {
    const jobs = await this.database.getNextJobs(1)

    if (jobs.length === 0) {
      return
    }

    // TODO: I am slowly migrating this behaviour to actors on top of a state machine
    // It currently works more like a job queue
    for (const job of jobs) {
      const stateActor = this.handlers.get(job.type)
      if (!stateActor) {
        console.warn(
          `No handler registered for job type: ${job.type}`
        )
        job.status = JobStatus.CANCELLED
        job.updatedAt = Date.now()
        await this.database.updateJob(job)
        continue
      }

      // Mark job as processing
      job.status = JobStatus.PROCESSING
      job.updatedAt = Date.now()
      await this.database.updateJob(job)
      this.emitJobEvent('job-started', job)
      try {
        await this.processJob(job, stateActor)
      } catch (error) {
        console.error('Error processing job:', error)
      }
    }
    await this.processJobs()
  }

  async resolveJobContext(
    job: Job,
    state: State
  ): Promise<Context> {
    return this.options.resolveContext
      ? await this.options.resolveContext(state, job)
      : ({} as Context)
  }

  async processJob(
    job: Job,
    stateActor: StateActor<unknown, State, Context, unknown>
  ): Promise<void> {
    let timeoutId
    if (job.timeout) {
      timeoutId = setTimeout(() => {
        if (this.runningJobs.has(job.id)) {
          this.runningJobs.delete(job.id)
          this.jobFailed(job, 'Timeout')
        }
      }, job.timeout) as unknown as number
    }

    this.runningJobs.set(job.id, { timeoutId })

    try {
      const state = this.hub?.getState() as State
      const context: Context =
        (await this.resolveJobContext(job, state)) ?? {}
      const result = await stateActor.process(
        job.data,
        {
          state,
          job,
          context,
        },
        {
          setState: (state: Partial<State>) =>
            this.setState(state),
        }
      )

      if (this.runningJobs.has(job.id)) {
        this.runningJobs.delete(job.id)
        if (timeoutId) clearTimeout(timeoutId)
        job.status = JobStatus.COMPLETED
        job.result = result
        job.updatedAt = Date.now()
        await this.database.updateJob(job)
        this.emitJobEvent('job-completed', job)
      }
    } catch (error: unknown) {
      if (this.runningJobs.has(job.id)) {
        this.runningJobs.delete(job.id)
        if (timeoutId) clearTimeout(timeoutId)
        await this.jobFailed(job, getErrorMessage(error))
      }
    }
  }

  async jobFailed(job: Job, error: string) {
    job.attempts++
    job.error = error
    job.updatedAt = Date.now()

    if (job.attempts >= job.maxAttempts) {
      job.status = JobStatus.FAILED
      this.emitJobEvent('job-failed', job)
      console.warn(`Job failed:`, job.error, job)
    } else {
      job.status = JobStatus.PENDING
      // Exponential backoff: wait 2^attempts seconds
      job.scheduledAt =
        Date.now() + Math.pow(2, job.attempts) * 1000
    }

    await this.database.updateJob(job)
  }

  async destroy(): Promise<void> {
    if (this.processingTimeout) {
      clearInterval(this.processingTimeout)
      this.processingTimeout = null
    }

    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
  }
}
